// Q9 S6 — Per-section validation hook combining client-side rules (D9
// advance gate) with server-side field errors (D8 8a inline).
//
// Inputs:
//   - currentSection: which section is rendered now
//   - formState: the wizard's reactive form state
//   - serverFieldErrors: API field name → messages, from the most
//     recent failed PATCH/submit response (cleared by the wizard when
//     the section's data changes — see D8 spec text "Persists until the
//     field changes")
//
// Outputs:
//   - mergedFieldErrors: client-side errors UNION server-side errors,
//     server messages winning (they're more authoritative — they
//     reflect what the API actually rejected this round-trip).
//   - isComplete: client-side gate passes (no client errors AND no
//     server errors lingering for this section).
//   - formErrors: client-side cross-field rules that don't anchor to
//     a single field; passed straight to SectionErrorBanner.
//
// The hook is a thin pure function wrapped in useMemo so the wizard's
// section components re-render only when the result actually changes.

import { useMemo } from "react";
import { validateSection, type SectionValidationResult } from "../lib/visit-report-section-validators.js";
import type { FormState } from "../lib/visit-report-types.js";
import type { SectionStep } from "../lib/visit-report-conditional-rules.js";

export interface UseSectionValidationResult extends SectionValidationResult {
  /** Convenience helper — true when the section is ready to be marked
   *  complete in the step indicator (no client OR server errors). */
  readyForCompletion: boolean;
}

export function useSectionValidation(
  section:           SectionStep,
  formState:         FormState,
  serverFieldErrors: ReadonlyMap<string, readonly string[]> = new Map(),
): UseSectionValidationResult {
  return useMemo(() => {
    const client = validateSection(section, formState);
    const merged = mergeFieldErrors(client.fieldErrors, serverFieldErrors);
    const ready  = client.isComplete && serverFieldErrors.size === 0;
    return {
      isComplete: client.isComplete,
      fieldErrors: merged,
      formErrors: client.formErrors,
      readyForCompletion: ready,
    };
  }, [section, formState, serverFieldErrors]);
}

/** Server messages WIN per-field; client messages fill in fields the
 *  server didn't flag (e.g., the carer fixed BP position but the
 *  cognitive_test_score gate still fires client-side). */
function mergeFieldErrors(
  client: ReadonlyMap<string, readonly string[]>,
  server: ReadonlyMap<string, readonly string[]>,
): ReadonlyMap<string, readonly string[]> {
  if (server.size === 0) return client;
  const out = new Map<string, readonly string[]>(client);
  for (const [field, msgs] of server) {
    out.set(field, msgs);
  }
  return out;
}
